<template>
  <div>
    <v-data-table
      :headers="dataTable.headers"
      :items="invoices ? invoices : getInvoiceContract"
      class="my-3 border-2 cursor-pointer"
      :items-per-page="10"
      :loading="isLoading"
      @click:row="rowClick"
      :item-class="itemRowClass"
      :no-data-text="$t('noinfo')"
      :no-results-text="$t('no_matching_records_found')"
    >
      <template v-slot:body.prepend>
        <tr>
          <td>
            <v-menu
              v-model="menuDate"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="filterDateLabel(filterData.date)"
                  :label="$t('date')"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  solo
                  dense
                  hide-details
                  :append-icon="filterData.date ? 'mdi-close' : null"
                  @click:append="filterData.date = null"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filterData.date"
                @input="menuDate = false"
                :header-date-format="$helper.headerDateFormat"
                :year-format="$helper.yearFormat"
                no-title
                locale="th"
                :weekday-format="$helper.formatWeekday" :month-format="$helper.formatMonth"
              ></v-date-picker>
            </v-menu>
          </td>
          <td>
            <v-menu
              v-model="menuDueDate"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="filterDateLabel(filterData.dueDate)"
                  :label="$t('invoice.installment_due_date')"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  solo
                  dense
                  hide-details
                  :append-icon="filterData.dueDate ? 'mdi-close' : null"
                  @click:append="filterData.dueDate = null"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="filterData.dueDate"
                @input="menuDueDate = false"
                :header-date-format="$helper.headerDateFormat"
                :year-format="$helper.yearFormat"
                no-title
                locale="th"
                :weekday-format="$helper.formatWeekday" :month-format="$helper.formatMonth"
              ></v-date-picker>
            </v-menu>
          </td>
          <td class="ps-0">
            <v-select v-model="filterData.documentType" item-text="text"
              item-value="value" :items="filterOption.documentType" type="text" :label="$t('type')" solo dense
              hide-details
              clearable
              :no-data-text="$t('noinfo')">
            </v-select>
          </td>
          <td class="ps-0">
            <v-text-field v-model="filterData.documentId" type="text" :label="$t('number')" solo dense hide-details clearable/>
          </td>
          <td class="ps-0">
            <v-text-field v-model="filterData.customerData" type="text" :label="$t('customer.title')" solo dense hide-details clearable/>
          </td>
          <td class="ps-0">
            <v-text-field v-model="filterData.totalValue" type="text" :label="$t('market.total_value')" solo dense hide-details clearable/>
          </td>
          <td class="ps-0">
            <v-text-field v-model="filterData.totalUnpaidValue" type="text" :label="$t('invoice.remaining_amount')" solo dense hide-details clearable/>
          </td>
          <td class="ps-0">
            <v-select v-model="filterData.status" item-text="text"
              item-value="value" :items="filterOption.status" type="text" :label="$t('status')" solo dense
              hide-details
              clearable
              :no-data-text="$t('noinfo')">
            </v-select>
          </td>
          <td></td>
        </tr>
      </template>
      
      <template v-slot:item.date="{ item }">
        <div class="d-flex align-center justify-center">
          <v-tooltip top color="primary" v-if="item.isSent && item.isViewed">
            <template v-slot:activator="{ on, attrs }">
              <div v-bind="attrs" v-on="on" style="margin-left: -10px; margin-top: -3px;">
                <v-btn color="primary" fab x-small dark elevation="0"
                  style="pointer-events: none; width: 15px; height: 15px;" class="me-1">
                  <v-icon style="font-size: 14px;">mdi-check-all</v-icon>
                </v-btn>
              </div>
            </template>
            <span>{{ $t('customer.customer_already_read') }}</span>
          </v-tooltip>
          <div v-else style="width: 13px;"></div>
          <template v-if="item.date">{{ $moment(item.date).format("DD/MM/YYYY") }}</template>
        </div>
      </template>
      <template v-slot:item.dueDate="{ item }">
        <template v-if="item.dueDate">
          {{ $moment(item.dueDate).format("DD/MM/YYYY") }}
        </template>
      </template>

      <template v-slot:item.status="{ item }">
        <v-chip :class="item.statusClass.class" :color="item.statusClass.color">{{ item.statusClass.text }}</v-chip>
        <v-chip class="ms-1 px-0 py-0" color="rgb(255, 137, 59, 0.3)" v-if="item.invoicePayment">
          <img width="32" :src="require(`../assets/images/money-sign-orange.svg`)" />
        </v-chip>
      </template>
      <template v-slot:item.totalValue="{ item }">
        {{ item.totalValue | currencyPrefixSign(item.currencyCode) }} 
      </template>
      <template v-slot:item.totalUnpaidValue="{ item }">
        {{ item.totalUnpaidValue | currencyPrefixSign(item.currencyCode) }} 
      </template>
      <template v-slot:item.note="{ item }">
        <v-tooltip left color="warning" v-if="cancelledNotes(item).length">
          <template v-slot:activator="{ on, attrs }">
            <div class="d-flex align-center justify-center" v-bind="attrs" v-on="on">
              <img :src="require(`@/assets/images/notepad-info.svg`)" :alt="$t('note')" />
              {{ cancelledNotes(item).length }}
            </div>
          </template>
          <div style="max-width: 330px;">
            <h4>
              {{ $t('payment.cancel_payment_count', { count: cancelledNotes(item).length }) }}
            </h4>
            <div v-for="(note, index) in cancelledNotes(item)" :key="note.documentId">
              <span><b>{{ $t('bill.receipt') }} {{ note.documentId }}: </b> {{ note.cancelledNote || "-" }}</span>
              <hr class="my-1" v-if="index < cancelledNotes(item).length - 1" />
            </div>
          </div>
        </v-tooltip>
      </template>
    </v-data-table>
    <invoice-dialog-component v-model="isInvoiceDialog" :invoiceId="invoiceId" :projectId="getProjectId"
      :contractData="getContract" @onSaveSuccess="reloadContract" @onRemoveSuccess="onRemoveInvoiceSuccess" />
  </div>
</template>


<script>
import { mapGetters } from "vuex";
import InvoiceDialogComponent from "./InvoiceDialog.component.vue";
export default {
  components: { InvoiceDialogComponent },
  computed: {
    ...mapGetters({
      getProjectId: "markets/getProjectId",
      getContract: "contract/getContract",
      getInvoiceContract: "invoice/getInvoiceContract",
    }),
  },
  methods: {
    rowClick(invoice) {
      this.invoiceId = invoice.objectId;
      this.isInvoiceDialog = true;
    },
    reloadContract() {
      this.$emit("onLoad");
    },
    onRemoveInvoiceSuccess() {
      this.reloadContract()
      this.invoiceId = ""
      this.isInvoiceDialog = false
    },
    itemRowClass(invoice) {
      if (invoice.isSent && invoice.isViewed) {
        return "bg-green-50";
      }
    },
    cancelledNotes(invoice) {
      let notes = []
      for (let receipt of invoice.receipts) {
        if (receipt.documentId && !receipt.isPaid) {
          notes.push({
            documentId: receipt.documentId,
            cancelledNote: receipt.cancelledNote
          })
        }
      }
      return notes
    },
    filterDateLabel(date){
      if (date) {
        return this.$moment(date).format('DD MMM YYYY')
      } else {
        return ""
      }
    }
  },
  data() {
    return {
      isInvoiceDialog: false,
      invoiceId: "",
      dataTable: {
        headers: [],
      },
      menuDate: false,
      menuDueDate: false,
      filterData: {
        date: null,
        dueDate: null,
        documentType: null,
        documentId: null,
        customerData: null,
        totalValue: null,
        totalUnpaidValue: null,
        note: null,
      },
      filterOption: {
        documentType: [
          {
            text: this.$t('bill.document_type.1')
          },
          {
            text: this.$t('bill.deposit_bill')
          },
          {
            text: this.$t('bill.checkin_bill')
          },
          {
            text: this.$t('payment.checkout_bill')
          },
          {
            text: this.$t('bill.other_bill')
          },
          {
            text: this.$t('payment.installment_bill')
          },
          {
            text: this.$t('payment.daily_booth')
          },
          {
            text: this.$t('payment.monthly_booking')
          }
        ],
        status: [
          {
            text: this.$t('payment.wait_approval'),
          },
          {
            text: this.$t('payment.paid'),
          },
          {
            text: this.$t('payment.pending'),
          },
          {
            text: this.$t('bill.edit_bill'),
          },
          {
            text: this.$t('bill.cancel'),
          }
        ]
      },
    };
  },
  props: {
    isLoading: Boolean,
    invoices: Array,
  },
  created() {
    this.dataTable.headers = [
      {
        text: this.$t('date'),
        value: "date",
        width: "10%",
        align: "center",
        filter: (d) => {
          if (this.filterData.date) {
            return d ? this.$moment(d).format('DD/MM/YYYY') == this.$moment(this.filterData.date).format('DD/MM/YYYY') : false;
          }
          return true;
        }
      },
      {
        text: this.$t('invoice.installment_due_date'),
        value: "dueDate",
        width: "10%",
        align: "center",
        filter: (d) => {
            if (this.filterData.dueDate) {
              return d ? this.$moment(d).format('DD/MM/YYYY') == this.$moment(this.filterData.dueDate).format('DD/MM/YYYY') : false;
            }
            return true;
          }
      },
      {
        text: this.$t('type'),
        value: "documentTypeString",
        align: "center",
        filter: d =>
          this.filterData.documentType
            ? d == this.filterData.documentType
            : d
      },
      { 
        value: "documentId", 
        text: this.$t('number'), 
        align: "center",
        filter: d =>
          this.filterData.documentId
            ? d
              ? d.toLowerCase().includes(this.filterData.documentId.toLowerCase())
              : false
            : true
      },
      { 
        value: "customerData.name", 
        text: this.$t('customer.title'), 
        filter: d =>
          this.filterData.customerData
            ? d
              .toLowerCase()
              .includes(this.filterData.customerData.toLowerCase())
            : d
      },
      { 
        value: "totalValue", 
        text: this.$t('market.total_value'), 
        align: "right",
        filter: d =>
          this.filterData.totalValue
            ? d.toString().includes(this.filterData.totalValue)
            : d || d === 0
      },
      { 
        value: "totalUnpaidValue", 
        text: this.$t('invoice.remaining_amount'),
        align: "right",
        filter: d =>
          this.filterData.totalUnpaidValue
            ? d.toString().includes(this.filterData.totalUnpaidValue)
            : d || d === 0
      },
      {
        value: "status",
        text: this.$t('status'),
        align: "center",
        filter: d =>
        this.filterData.status ? d == this.filterData.status : d
      },
      { 
        value: "note", 
        text: this.$t('note'), 
        width: "4%", 
        align: "center",
      },
    ];
  },
};
</script>
